export const ANONYMOUS_USER_ID = "anonymous";
export const EVERYONE_GROUP_NAME = "Everyone";

export const ADMINISTRATOR = "Administrator";
export const ADMINCENTER_GROUP_NAME = "Admin Center Administrators";
export const ADMINCENTER_PRODUCT_NAME = "Optimizely Admin Center";
export const DUPLICATE_GROUP_ERROR_MESSAGE = "Group/team name already exists. Please choose another name.";
export const DUPLICATE_EMAIL_ERROR_MESSAGE = "Unable to add user at this time. Please contact your admin.";
export const INVITATION_ERROR_MESSAGE = "An error occurred while sending invitation.";
export const INVITATION_SUCCESS_MESSAGE = "Invitation successfully sent.";
export const PERMISSIONS_UPDATE_SUCCESS_MESSAGE = "Permissions were updated successfully.";
export const UNLOCK_USER_SUCCESS_MESSAGE = "User successfully unlocked.";
export const ACTIVATE_USER_SUCCESS_MESSAGE = "User successfully activated.";
export const ADD_GROUP_ACCESS_SUCCESS_MESSAGE = "Group access successfully added.";
export const REMOVE_GROUP_ACCESS_SUCCESS_MESSAGE = "Group access successfully removed.";
export const AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE =
    "We could not determine which group grants this role on the selected instance, so no access was changed. Please contact support.";
export const ACCOUNT_ACTIVATION_UNKNOWN_ERROR_MESSAGE =
    "Unable to perform the requested operation, please try again later.";
export const ADMINCENTER_PRODUCT_ID = "404d1878bae04092a24d9f3a032769be";

export const ALL_PAGES_SIZE = 10000;
export const BATCHED_PAGE_SIZE = 2000;
export const DEFAULT_PAGE_SIZE = 20;

export const DEFAULT_DEBOUNCE = 500;
export const MIN_SEARCH_QUERY_LENGTH = 2;
export const ROLES_WARNING = "Managing roles is only available for certain products at this time";
export const ROLES_HIDE_WARNING_STORAGE_KEY = "admin-center.roles:hide-warning";
export const TOASTER_EVENT_NAME = "@admin-center:toast";
export const ADMINCENTER_ITEM_UPDATED_EVENT_NAME = "@admin-center:item-updated";
export const REFETCH_PERMISSIONS_EVENT_NAME = "@opti-product-switcher:refetch";
export const ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME = "@admin-center:custom-group-access-review";
export const ALL = "All";
export const APPLY = "Apply";
export const GROUP_TYPES_FILTER_IDS = "groupTypes_filter_ids";
export const GROUP_TYPES_SAVE_FILTERS = "groupTypes_save_filters";
export const GROUP_TYPES_FILTER_QUERY_PARAM = "types";
export const GROUP_FILTERS_PRODUCT_QUERY_PARAM = "pid";
export const GROUP_FILTERS_INSTANCE_QUERY_PARAM = "iid";
export const GROUP_FILTERS_ROLE_QUERY_PARAM = "rid";

export const ALL_PROJECTS_ID = "allProjects";
export const ALL_PROJECTS_NAME = "All Projects";
export const SAML = "SAML";
export const OIDC = "OIDC";

export const NONE = "None";
export const NOT_SET = "Not set";
export const NO_RESTRICTIONS = "No restrictions";
export const SYSTEM_ROLE_TYPE = "ProductDefined";
export const UNEXPECTED_ERROR = "An unexpected error occurred. Please try again.";
export const CUSTOMER_DEFINED_PRODUCT_ROLE = "CustomerDefined_Product";
export const CUSTOMER_DEFINED_INSTANCE_ROLE = "CustomerDefined_Instance";
export const EXP_PERMISSIONS_KEY = "permissions";

export const SSO_CONNECTION_LIMIT = 5;
export const ALL_INSTANCES_OPTION = { name: ALL, id: "", nickname: "All instances (current and future)" };

export const TOGGLE_ALL_INSTANCES_OPTION = { name: "Select all instances", id: "", nickname: "" };

export const ROLE_MAPPINGS: { [index: string]: string } = {
    default: "Custom",
    CustomerDefined_Product: "Custom",
    ProductDefined: "System",
    CustomerDefined_Instance: "Custom"
};

export const enum CONTEXT_SCOPES {
    ORGANIZATION = "organization",
    PRODUCT = "product",
    INSTANCE = "instance",
    PROJECT = "project"
}

export const enum GROUP_TYPES {
    PRODUCT = "product",
    INTERNAL = "internal",
    CUSTOM = "custom"
}

export const enum ACCESS_TYPES {
    SUPER_ADMIN = "All",
    PRODUCT = "Product",
    INSTANCE = "Instance",
    PROJECT = "Project"
}

export const enum EVENT_TYPES {
    UPDATE_USER = "update-user",
    ADD_USER = "add-user",
    DELETE_USER = "delete-user",
    BULK_DELETE_USER = "bulk-delete-user",

    UPDATE_GROUP = "update-group",
    ADD_GROUP = "add-group",
    DELETE_GROUP = "delete-group",

    UPDATE_ROLE = "update-role",
    ADD_ROLE = "add-role",
    DELETE_ROLE = "delete-role",

    UPDATE_INSTANCE = "update-instance",

    ADD_USER_TO_GROUP = "add-user-to-group",
    REMOVE_USER_FROM_GROUP = "remove-user-from-group",

    REMOVE_INSTANCE_FROM_GROUP = "remove-instance-from-group",
    MODIFY_INSTANCE_PERMISSIONS_IN_GROUP = "modify-instance-permissions-in-group",

    ADD_TEAM = "add-team",
    DELETE_TEAM = "delete-team",
    UPDATE_TEAM = "update-team",

    ADD_BULK_UPLOAD = "add-bulk-upload"
}

export const ATTRIBUTE_SCOPES = {
    PROJECT: "AttributeId",
    INSTANCE: "InstanceId",
    PRODUCT: "ProductId"
};
export const ATTRIBUTE_ROLES = {
    GROUPS: {
        CREATE: "Create Groups",
        READ: "Read Groups",
        UPDATE: "Update Groups",
        MANAGE: "Manage Group Members"
    },
    INVITATIONS: {
        CREATE: "Create Invitations",
        READ: "Read Invitations",
        UPDATE: "Update Invitations"
    },
    PRODUCTS: {
        READ: "Read Products"
    },
    ROLES: {
        CREATE: "Create Roles",
        READ: "Read Roles",
        UPDATE: "Update Roles"
    },
    USERS: {
        READ: "Read Users",
        UPDATE: "Update Users"
    },
    BILLING: {
        READ: "Read Billing Dashboard"
    },
    SETTINGS: {
        UPDATE: "Manage Settings"
    },
    INSTANCE_DETAILS: {
        UPDATE: "Manage Instance Details"
    }
};

export const INVITATION_STATUS: { [index: string]: string } = {
    EXPIRED: "Expired",
    REVOKED: "Revoked",
    PENDING: "Pending",
    ACCEPTED: "Accepted"
};

/**
 * Lifecycle status of a bulk user CSV upload, as returned by the User Management API
 * (`BulkUploadStatus` in idx-user-mgmt-api). Values are sent as-is on the wire.
 */
export const BULK_UPLOAD_STATUS = {
    UPLOADED: "Uploaded",
    PROCESSING: "Processing",
    COMPLETED: "Completed",
    FAILED: "Failed"
} as const;

/**
 * Processing outcome written by the downstream blob-trigger job. Absent from the response
 * until the upload has been processed.
 *
 * `PartiallySucceeded` covers two cases the job treats alike: some rows enrolled and some failed, and
 * every row enrolled but one or more target user groups were rejected. Either way the lifecycle
 * `Status` is still `Completed` — the outcome lives here.
 */
export const BULK_UPLOAD_RESULT_STATUS = {
    SUCCEEDED: "Succeeded",
    PARTIALLY_SUCCEEDED: "PartiallySucceeded",
    FAILED: "Failed"
} as const;

export type BulkUploadStatus = (typeof BULK_UPLOAD_STATUS)[keyof typeof BULK_UPLOAD_STATUS];
export type BulkUploadResultStatus = (typeof BULK_UPLOAD_RESULT_STATUS)[keyof typeof BULK_UPLOAD_RESULT_STATUS];

/**
 * How often the upload detail view re-reads a row that has not reached a terminal status, in ms.
 * Processing is measured in seconds to minutes, so this is frequent enough to look live without
 * polling a long-running import hundreds of times.
 */
export const BULK_UPLOAD_POLL_INTERVAL = 5000;

export const INVITATION_RESEND_ERROR_MESSAGE =
    "Invitation was unable to be resent at this time. Please try again later or contact your administrator.";
export const INVITATION_REVOKE_ERROR_MESSAGE =
    "Invitation was unable to be revoked at this time. Please try again later or contact your administrator.";

export const OPTIMIZELY_ORG_ID = process.env.REACT_APP_OPTIMIZELY_ORG_ID;

export const EXP_PROJECT_TYPES = { WEB: "Web", LEGACY: "Legacy Full Stack", FEATURE: "Feature Experimentation" };

export const EXP_ROLE_PERMISSION_MAPPING: { [index: string]: string[] } = {
    Publisher: [EXP_PROJECT_TYPES.WEB, EXP_PROJECT_TYPES.LEGACY, EXP_PROJECT_TYPES.FEATURE],
    "Restricted Publisher": [EXP_PROJECT_TYPES.WEB, EXP_PROJECT_TYPES.LEGACY],
    Editor: [EXP_PROJECT_TYPES.WEB, EXP_PROJECT_TYPES.LEGACY, EXP_PROJECT_TYPES.FEATURE],
    "Restricted Editor": [EXP_PROJECT_TYPES.WEB, EXP_PROJECT_TYPES.LEGACY],
    Viewer: [EXP_PROJECT_TYPES.WEB, EXP_PROJECT_TYPES.LEGACY, EXP_PROJECT_TYPES.FEATURE]
};

export const EXP_PROJECT_OWNER_PERMISSION = "Project Owner";

export const GUID_PATTERN = /\b[a-f\d]{32}\b/g;
export const URL_PATTERN =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

export const UNSAVED_CHANGES_CONFIRMATION_DO_NOT_SHOW_AGAIN = "admin-center:do-not-show-unsaved-confirmation";

export const NOT_FOUND = "Not Found";

export const USER_EXTERNAL_STATUS: { [index: string]: string } = {
    ACTIVE: "Active",
    DEPROVISIONED: "Deactivated",
    LOCKEDOUT: "Locked Out",
    PASSWORDEXPIRED: "Password Expired",
    PROVISIONED: "Pending User Action",
    RECOVERY: "Password Reset",
    STAGED: "Staged",
    SUSPENDED: "Suspended",
    NOTFOUND: NOT_FOUND
};

export const enum SWR_KEY_PREFIX {
    USERS = "list-of-users:",
    GROUPS = "list-of-groups:",
    ROLES = "list-of-roles:",
    INSTANCES = "list-of-instances:",
    TEAMS = "list-of-teams", // no colon as it's also used as UpdateSynchronizer event key (as the others should), colon is added in SWR key prefix
    AGENTS = "list-of-agents",
    BULK_UPLOADS = "list-of-bulk-uploads"
}

export const enum CUSTOM_GROUP_ACCESS_ACTION {
    ADD = "Add Access",
    REMOVE = "Remove Access"
}

export const CMS_SAAS_DASHBOARD_PRODUCT_TAB_NAME = "CMS SaaS";
export const CMS_PAAS_DASHBOARD_PRODUCT_TAB_NAME = "CMS PaaS";

export const INVITATION_REVOKE_DAYS = 30;
export const SEARCH_DROPDOWN_THRESHOLD = 10;
export const NEW_SUPPORT_TICKET_HREF = "https://support.optimizely.com/hc/en-us/requests/new";

export const FAILED_OPERATION_TYPES = {
    USER_GROUP_ADD_USER: "user-group-add-user",
    USER_GROUP_REMOVE_USER: "user-group-remove-user",
    USER_GROUP_ADD_INSTANCE_PERMISSION: "user-group-add-instance-permission",
    USER_GROUP_DELETE_INSTANCE_PERMISSION: "user-group-delete-instance-permission",
    USER_GROUP_UPDATE_INSTANCE_PERMISSION: "user-group-update-instance-permission",
    USER_GROUP_UPDATE_METADATA: "user-group-update-metadata"
};

export const PROPERTY_KEYS = {
    TEAM: "isTeam"
};

export const ATTRIBUTE_KEYS = {
    TEAM: "Team"
};
